import React, { useState } from 'react';
import { upsertDiseaseProfile } from '../lib/diseaseProfileService';
import { setOnboardingCompleted } from '../lib/profileService';

const GRADIENT = 'linear-gradient(135deg, #6B7FFF 0%, #8B5CF6 100%)';
const WZJG_LOCATIONS = ['Proctitis', 'Lewostronne', 'Rozległe', 'Pancolitis'];
const CROHN_LOCATIONS = ['Jelito cienkie', 'Jelito grube', 'Odbytnica', 'Górny odcinek przewodu pokarmowego', 'Mieszane'];
const DIET_OPTIONS = ['Normalna', 'Low-FODMAP', 'SCD', 'Bezglutenowa', 'Bezlaktozowa', 'Inna'];

interface OnboardingProps {
  userName: string;
  userId: string;
  onComplete: () => void;
}

interface DiseaseData {
  disease_type?: string;
  diagnosis_date?: string | null;
  diagnosed_by?: string | null;
  disease_location?: string[];
  disease_location_other?: string | null;
  last_flare_date?: string | null;
  last_colonoscopy_date?: string | null;
  avg_bowel_movements_remission?: number | null;
  has_stoma?: boolean | null;
  bowel_surgeries?: boolean | null;
  bowel_surgeries_description?: string | null;
  food_allergies?: string | null;
  diet?: string[];
  diet_other?: string | null;
  doctor_name?: string | null;
  hospital?: string | null;
}

const rem = (px: number) => `${px / 16}rem`;

export const Onboarding: React.FC<OnboardingProps> = ({ userName, userId, onComplete }) => {
  const [step, setStep] = useState(1);
  const [data, setData] = useState<DiseaseData>({ disease_location: [], diet: [] });
  const [saving, setSaving] = useState(false);

  const totalSteps = 7;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const toggleArray = (arr: string[], item: string) =>
    arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: rem(14),
    border: '1px solid #e2e8f0',
    borderRadius: rem(12),
    fontSize: rem(16),
    boxSizing: 'border-box'
  };
  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: rem(8), fontWeight: 600, color: '#475569', fontSize: rem(14) };
  const btnPrimary: React.CSSProperties = {
    padding: `${rem(12)} ${rem(24)}`,
    background: GRADIENT,
    color: 'white',
    border: 'none',
    borderRadius: rem(12),
    fontSize: rem(16),
    fontWeight: 600,
    cursor: 'pointer'
  };
  const btnSecondary: React.CSSProperties = {
    ...btnPrimary,
    background: 'transparent',
    color: '#6B7FFF',
    border: '2px solid #6B7FFF'
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      await upsertDiseaseProfile(userId, data);
      await setOnboardingCompleted(userId);
      onComplete();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: rem(24),
    boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12)',
    width: '100%',
    maxWidth: '480px',
    maxHeight: 'calc(100vh - 48px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #e8ecff 0%, #f3e8ff 50%, #ede9fe 100%)',
        padding: rem(24)
      }}
    >
      <div style={cardStyle}>
        <div style={{ marginBottom: rem(24), flexShrink: 0, padding: isMobile ? rem(24) : rem(40), paddingBottom: 0 }}>
          <div style={{ fontSize: rem(12), color: '#6B7FFF', fontWeight: 600, marginBottom: rem(4) }}>
            Krok {step} z {totalSteps}
          </div>
          <div
            style={{
              height: rem(6),
              background: '#e2e8f0',
              borderRadius: rem(3),
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                width: `${(step / totalSteps) * 100}%`,
                height: '100%',
                background: GRADIENT,
                transition: 'width 0.3s ease'
              }}
            />
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            padding: `0 ${isMobile ? rem(24) : rem(40)}`
          }}
        >
        {/* Krok 1: Powitanie */}
        {step === 1 && (
          <div style={{ animation: 'fadeIn 0.3s ease' }}>
            <h2 style={{ fontSize: rem(24), fontWeight: 700, color: '#1e293b', marginBottom: rem(16) }}>
              Cześć {userName || 'Użytkowniku'}!
            </h2>
            <p style={{ color: '#64748b', lineHeight: 1.6, marginBottom: rem(24) }}>
              Zanim zaczniesz, pomóż nam lepiej poznać Twoją historię choroby.
            </p>
            <p style={{ color: '#64748b', fontSize: rem(14), lineHeight: 1.6 }}>
              Te dane posłużą do generowania raportów dla lekarza oraz analizy Twojego stanu zdrowia. Możesz je uzupełnić lub edytować później w zakładce Moje konto → Historia choroby.
            </p>
          </div>
        )}

        {/* Krok 2: Diagnoza */}
        {step === 2 && (
          <div>
            <h2 style={{ fontSize: rem(20), fontWeight: 600, color: '#1e293b', marginBottom: rem(20) }}>Diagnoza</h2>
            <label style={labelStyle}>Typ choroby</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: rem(12), marginBottom: rem(20) }}>
              {['WZJG', 'Choroba Leśniowskiego-Crohna', 'Inne'].map((t) => (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: rem(8), cursor: 'pointer' }}>
                  <input type="radio" name="dt" checked={data.disease_type === t} onChange={() => setData((p) => ({ ...p, disease_type: t }))} />
                  <span>{t}</span>
                </label>
              ))}
            </div>
            <label style={labelStyle}>Data rozpoznania</label>
            <input type="date" value={data.diagnosis_date || ''} onChange={(e) => setData((p) => ({ ...p, diagnosis_date: e.target.value || null }))} style={{ ...inputStyle, marginBottom: rem(20) }} />
            <label style={labelStyle}>Kto postawił diagnozę</label>
            <input type="text" value={data.diagnosed_by || ''} onChange={(e) => setData((p) => ({ ...p, diagnosed_by: e.target.value || null }))} style={inputStyle} placeholder="np. gastroenterolog" />
          </div>
        )}

        {/* Krok 3: Lokalizacja */}
        {step === 3 && (
          <div>
            <h2 style={{ fontSize: rem(20), fontWeight: 600, color: '#1e293b', marginBottom: rem(20) }}>Lokalizacja zmian</h2>
            {data.disease_type === 'WZJG' && (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: rem(8), marginBottom: rem(12) }}>
                  {WZJG_LOCATIONS.map((loc) => (
                    <label key={loc} style={{ display: 'flex', alignItems: 'center', gap: rem(6), cursor: 'pointer' }}>
                      <input type="checkbox" checked={(data.disease_location || []).includes(loc)} onChange={() => setData((p) => ({ ...p, disease_location: toggleArray(p.disease_location || [], loc) }))} />
                      <span style={{ fontSize: rem(14) }}>{loc}</span>
                    </label>
                  ))}
                </div>
                <label style={labelStyle}>Inne</label>
                <input type="text" value={data.disease_location_other || ''} onChange={(e) => setData((p) => ({ ...p, disease_location_other: e.target.value || null }))} style={inputStyle} />
              </>
            )}
            {data.disease_type === 'Choroba Leśniowskiego-Crohna' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: rem(8) }}>
                {CROHN_LOCATIONS.map((loc) => (
                  <label key={loc} style={{ display: 'flex', alignItems: 'center', gap: rem(6), cursor: 'pointer' }}>
                    <input type="checkbox" checked={(data.disease_location || []).includes(loc)} onChange={() => setData((p) => ({ ...p, disease_location: toggleArray(p.disease_location || [], loc) }))} />
                    <span style={{ fontSize: rem(14) }}>{loc}</span>
                  </label>
                ))}
              </div>
            )}
            {data.disease_type === 'Inne' && (
              <>
                <label style={labelStyle}>Opis lokalizacji</label>
                <input type="text" value={data.disease_location_other || ''} onChange={(e) => setData((p) => ({ ...p, disease_location_other: e.target.value || null }))} style={inputStyle} />
              </>
            )}
            {!data.disease_type && <p style={{ color: '#94a3b8' }}>Wybierz typ choroby w poprzednim kroku</p>}
          </div>
        )}

        {/* Krok 4: Aktywność */}
        {step === 4 && (
          <div>
            <h2 style={{ fontSize: rem(20), fontWeight: 600, color: '#1e293b', marginBottom: rem(20) }}>Aktywność choroby</h2>
            <label style={labelStyle}>Data ostatniego rzutu</label>
            <input type="date" value={data.last_flare_date || ''} onChange={(e) => setData((p) => ({ ...p, last_flare_date: e.target.value || null }))} style={{ ...inputStyle, marginBottom: rem(20) }} />
            <label style={labelStyle}>Data ostatniej kolonoskopii</label>
            <input type="date" value={data.last_colonoscopy_date || ''} onChange={(e) => setData((p) => ({ ...p, last_colonoscopy_date: e.target.value || null }))} style={inputStyle} />
          </div>
        )}

        {/* Krok 5: Dane ogólne */}
        {step === 5 && (
          <div>
            <h2 style={{ fontSize: rem(20), fontWeight: 600, color: '#1e293b', marginBottom: rem(20) }}>Dane ogólne</h2>
            <label style={labelStyle}>Średnia liczba wypróżnień na dobę w remisji</label>
            <input type="number" min={0} value={data.avg_bowel_movements_remission ?? ''} onChange={(e) => setData((p) => ({ ...p, avg_bowel_movements_remission: e.target.value ? parseInt(e.target.value, 10) : null }))} style={{ ...inputStyle, marginBottom: rem(20) }} />
            <label style={labelStyle}>Czy jest stomia</label>
            <div style={{ display: 'flex', gap: rem(16), marginBottom: rem(20) }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: rem(8), cursor: 'pointer' }}><input type="radio" name="stoma" checked={data.has_stoma === true} onChange={() => setData((p) => ({ ...p, has_stoma: true }))} /> Tak</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: rem(8), cursor: 'pointer' }}><input type="radio" name="stoma" checked={data.has_stoma === false} onChange={() => setData((p) => ({ ...p, has_stoma: false }))} /> Nie</label>
            </div>
            <label style={labelStyle}>Operacje jelitowe</label>
            <div style={{ display: 'flex', gap: rem(16), marginBottom: rem(12) }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: rem(8), cursor: 'pointer' }}><input type="radio" name="surgeries" checked={data.bowel_surgeries === true} onChange={() => setData((p) => ({ ...p, bowel_surgeries: true }))} /> Tak</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: rem(8), cursor: 'pointer' }}><input type="radio" name="surgeries" checked={data.bowel_surgeries === false} onChange={() => setData((p) => ({ ...p, bowel_surgeries: false }))} /> Nie</label>
            </div>
            {data.bowel_surgeries === true && (
              <>
                <label style={labelStyle}>Opis operacji</label>
                <input type="text" value={data.bowel_surgeries_description || ''} onChange={(e) => setData((p) => ({ ...p, bowel_surgeries_description: e.target.value || null }))} style={{ ...inputStyle, marginBottom: rem(20) }} />
              </>
            )}
            <label style={labelStyle}>Alergie pokarmowe i nietolerancje</label>
            <input type="text" value={data.food_allergies || ''} onChange={(e) => setData((p) => ({ ...p, food_allergies: e.target.value || null }))} style={{ ...inputStyle, marginBottom: rem(20) }} placeholder="np. gluten, laktoza" />
            <label style={labelStyle}>Dieta</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: rem(8), marginBottom: rem(12) }}>
              {DIET_OPTIONS.map((d) => (
                <label key={d} style={{ display: 'flex', alignItems: 'center', gap: rem(6), cursor: 'pointer' }}>
                  <input type="checkbox" checked={(data.diet || []).includes(d)} onChange={() => setData((p) => ({ ...p, diet: toggleArray(p.diet || [], d) }))} />
                  <span style={{ fontSize: rem(14) }}>{d}</span>
                </label>
              ))}
            </div>
            {(data.diet || []).includes('Inna') && (
              <>
                <label style={labelStyle}>Inna dieta</label>
                <input type="text" value={data.diet_other || ''} onChange={(e) => setData((p) => ({ ...p, diet_other: e.target.value || null }))} style={inputStyle} />
              </>
            )}
          </div>
        )}

        {/* Krok 6: Lekarz */}
        {step === 6 && (
          <div>
            <h2 style={{ fontSize: rem(20), fontWeight: 600, color: '#1e293b', marginBottom: rem(20) }}>Lekarz prowadzący</h2>
            <label style={labelStyle}>Imię i nazwisko gastroenterologa</label>
            <input type="text" value={data.doctor_name || ''} onChange={(e) => setData((p) => ({ ...p, doctor_name: e.target.value || null }))} style={{ ...inputStyle, marginBottom: rem(20) }} />
            <label style={labelStyle}>Szpital/przychodnia</label>
            <input type="text" value={data.hospital || ''} onChange={(e) => setData((p) => ({ ...p, hospital: e.target.value || null }))} style={inputStyle} />
          </div>
        )}

        {/* Krok 7: Zakończenie */}
        {step === 7 && (
          <div>
            <h2 style={{ fontSize: rem(24), fontWeight: 700, color: '#1e293b', marginBottom: rem(16) }}>Gotowe!</h2>
            <p style={{ color: '#64748b', lineHeight: 1.6 }}>Twój profil jest skonfigurowany. Możesz teraz korzystać z aplikacji.</p>
          </div>
        )}
        </div>

        {/* Przyciski - zawsze widoczne na dole */}
        <div style={{ display: 'flex', gap: rem(12), marginTop: rem(24), flexWrap: 'wrap', flexShrink: 0, padding: isMobile ? rem(24) : rem(40), paddingTop: 0 }}>
          {step > 1 && (
            <button type="button" onClick={() => setStep((s) => s - 1)} style={btnSecondary}>
              Wstecz
            </button>
          )}
          <div style={{ flex: 1, minWidth: rem(120) }} />
          {step < totalSteps ? (
            <>
              {step > 1 && (
                <button type="button" onClick={() => setStep((s) => s + 1)} style={btnSecondary}>
                  Pomiń
                </button>
              )}
              <button type="button" onClick={() => setStep((s) => s + 1)} style={btnPrimary}>
                {step === 1 ? 'Zaczynajmy' : 'Dalej'}
              </button>
            </>
          ) : (
            <button type="button" onClick={handleFinish} disabled={saving} style={btnPrimary}>
              {saving ? 'Zapisywanie...' : 'Przejdź do aplikacji'}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
